import type { CheckoutSessionDto } from '@reelplicate/api-client/model';
import { useEffect, useEffectEvent, useState } from 'react';

import { type CheckoutTotals, mountCheckout } from './checkout';

export type CheckoutStatus = 'loading' | 'ready' | 'completed' | 'error';

/**
 * Mounts the provider's checkout for `session` into the element given to
 * `mountOn` (a callback ref), and removes it on unmount or when the session
 * changes. `onCompleted` fires once the provider has the payment details.
 */
export function useCheckout(
  session: CheckoutSessionDto | undefined,
  onCompleted: () => void,
) {
  const [container, mountOn] = useState<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<CheckoutStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<CheckoutTotals | null>(null);
  const complete = useEffectEvent(() => {
    setStatus('completed');
    onCompleted();
  });

  useEffect(() => {
    if (!session || !container) return;
    let unmount: (() => void) | undefined;
    let cancelled = false;
    mountCheckout(session, container, {
      onReady: () => setStatus('ready'),
      onCompleted: complete,
      onTotals: setTotals,
      onError: (message) => {
        setStatus('error');
        setError(message);
      },
    }).then(
      (remove) => {
        if (cancelled) remove();
        else unmount = remove;
      },
      (reason: unknown) => {
        if (cancelled) return;
        setStatus('error');
        setError(
          reason instanceof Error ? reason.message : 'The payment form couldn’t load.',
        );
      },
    );
    return () => {
      cancelled = true;
      unmount?.();
      setStatus('loading');
      setError(null);
      setTotals(null);
    };
  }, [session, container]);

  return { mountOn, status, error, totals };
}
